import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Plus, Lock } from "lucide-react";

export default function Roles() {
  const { hasPerm } = useAuth();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null);
  const canManage = hasPerm("roles.manage");

  const load = async () => {
    const [r, c] = await Promise.all([api.get("/roles"), api.get("/users/permissions-catalog")]);
    setRoles(r.data); setCatalog(c.data);
  };
  useEffect(() => { load(); }, []);

  const grouped = catalog.reduce((acc, p) => {
    (acc[p.module] ||= []).push(p); return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="roles-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-display font-extrabold tracking-tight text-white">Cargos & RBAC</h1>
          <p className="text-zinc-400 mt-1">Cargos dinâmicos com matriz de permissões.</p>
        </div>
        {canManage && (
          <Button data-testid="new-role-btn" onClick={() => setEditing({ name: "", description: "", permissions: [] })}
            className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>
            <Plus size={16} className="mr-2" />Novo Cargo
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {roles.map((r) => (
          <div key={r.id} data-testid={`role-card-${r.slug}`} className="alpha-card p-5">
            <div className="flex items-center gap-3 mb-2">
              <span className="h-9 w-9 rounded-lg bg-gold/10 flex items-center justify-center"><ShieldCheck size={17} className="text-gold" /></span>
              <div className="flex-1">
                <div className="text-white font-semibold flex items-center gap-2">{r.name}
                  {r.system && <Lock size={12} className="text-zinc-500" />}</div>
                <div className="text-xs text-zinc-500">{r.description}</div>
              </div>
            </div>
            <div className="text-xs text-zinc-400 mb-3">
              {r.permissions.includes("*") ? "Acesso total (*)" : `${r.permissions.length} permissões`}
            </div>
            {canManage && r.slug !== "admin" && (
              <Button data-testid={`edit-role-${r.slug}`} size="sm" variant="ghost" className="text-gold-bright"
                onClick={() => setEditing({ ...r })}>Editar permissões</Button>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <RoleDialog role={editing} grouped={grouped} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function RoleDialog({ role, grouped, onClose, onSaved }) {
  const isNew = !role.id;
  const [form, setForm] = useState({ name: role.name, description: role.description || "", permissions: role.permissions || [] });
  const [saving, setSaving] = useState(false);

  const toggle = (key) => setForm((f) => ({
    ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((k) => k !== key) : [...f.permissions, key],
  }));

  const save = async () => {
    if (!form.name) { toast.error("Informe o nome do cargo"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/roles", form);
      else await api.put(`/roles/${role.id}`, form);
      toast.success("Cargo salvo");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-2xl" data-testid="role-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Novo Cargo" : `Editar: ${role.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs text-zinc-400">Nome</Label>
              <Input data-testid="role-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            <div><Label className="text-xs text-zinc-400">Descrição</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          </div>
          <Label className="text-xs text-zinc-400 block">Matriz de Permissões</Label>
          {Object.entries(grouped).map(([mod, perms]) => (
            <div key={mod} className="border border-white/10 rounded-xl p-3">
              <div className="text-sm font-medium text-gold-bright mb-2">{mod}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {perms.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.permissions.includes(p.key)} onChange={() => toggle(p.key)}
                      data-testid={`perm-${p.key}`} className="accent-yellow-500 h-4 w-4" />
                    <span className="text-zinc-300">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          <Button data-testid="save-role-btn" onClick={save} disabled={saving} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
